import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [originalAdmin, setOriginalAdmin] = useState(null);

  useEffect(() => {
    // Check for stored session
    const storedUser = localStorage.getItem('currentUser');
    const storedAdmin = localStorage.getItem('originalAdmin');
    const storedToken = localStorage.getItem('accessToken');
    
    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
    }
    if (storedAdmin) {
      setOriginalAdmin(JSON.parse(storedAdmin));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const { access_token, user: userData } = response.data;
      
      // Store token and user
      localStorage.setItem('accessToken', access_token);
      localStorage.setItem('currentUser', JSON.stringify(userData));
      
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    }
  };

  const register = async (data) => {
    try {
      // No login yet: the account is activated by the link we email to the address they signed up with.
      const response = await authAPI.register(data);
      return { success: true, requiresVerification: true, message: response.data?.message };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    setUser(null);
    setOriginalAdmin(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('originalAdmin');
  };

  const loginAsUser = async (targetUser) => {
    if (user?.role === 'admin' || user?.role === 'superuser') {
      try {
        // Store original admin token before switching
        const originalToken = localStorage.getItem('accessToken');
        
        const response = await authAPI.loginAsUser(targetUser.id || targetUser.userId);
        const { access_token, user: userData } = response.data;
        
        // Store original admin with their token
        setOriginalAdmin({ ...user, token: originalToken });
        localStorage.setItem('originalAdmin', JSON.stringify({ ...user, token: originalToken }));
        
        // Update to new user
        localStorage.setItem('accessToken', access_token);
        localStorage.setItem('currentUser', JSON.stringify(userData));
        setUser(userData);
        
        return { success: true };
      } catch (error) {
        return { success: false, error: error.response?.data?.detail || 'Failed to login as user' };
      }
    }
    return { success: false, error: 'Unauthorized' };
  };

  const returnToAdmin = async () => {
    if (originalAdmin) {
      // Restore the original admin's token and user data
      if (originalAdmin.token) {
        localStorage.setItem('accessToken', originalAdmin.token);
      }
      
      // Remove token from the admin object before storing
      const { token, ...adminData } = originalAdmin;
      localStorage.setItem('currentUser', JSON.stringify(adminData));
      setUser(adminData);
      setOriginalAdmin(null);
      localStorage.removeItem('originalAdmin');
      
      return { success: true };
    }
    return { success: false };
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    loginAsUser,
    returnToAdmin,
    originalAdmin,
    isAdmin: user?.role === 'admin' || user?.role === 'superuser',
    isSuperuser: user?.role === 'superuser',
    isLoggedInAsUser: !!originalAdmin,
    isSubUser: !!user?.isSubUser,
    permissions: user?.permissions || null,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
